import { useCallback, useMemo, useState } from 'react';
import { Modal, message, Tabs, Typography } from 'antd';
import {
  AppstoreOutlined, BgColorsOutlined, BlockOutlined, ShopOutlined,
  CheckCircleOutlined, DeleteOutlined, EditOutlined,
  ExclamationCircleOutlined, EyeOutlined, PlusOutlined, PoweroffOutlined
} from '@ant-design/icons';
import {
  ErpDataGrid, ErpMasterShell, useErpMasterQuery
} from '../../../../components/common/erp-grid';
import useAuth from '../../../../hooks/useAuth';

import { categoryApi, materialTypeApi, polishTypeApi, brandApi } from './itemConfigApi';
import { buildCategoryColumns } from './itemCategoryColumns';
import { buildMaterialTypeColumns } from './materialTypeColumns';
import { buildPolishTypeColumns } from './polishTypeColumns';
import { buildBrandColumns } from './brandColumns';
import ItemCategoryModal from './ItemCategoryModal';
import MaterialTypeModal from './MaterialTypeModal';
import PolishTypeModal from './PolishTypeModal';
import BrandModal from './BrandModal';

const { Title } = Typography;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Item Configuration Masters page (Stage 6B.8A-2A).
//
// Three tabs share the same shell:
//   1. Categories
//   2. Material Types
//   3. Polish Types
//
// Each tab is a self-contained component with its own query state.
// Permissions read off /me — wildcard '*' (master) covers all keys.
// ─────────────────────────────────────────────────────────────────

function usePerms() {
  const { me } = useAuth();
  return useMemo(() => {
    const list = Array.isArray(me?.permissions) ? me.permissions : [];
    const set = new Set(list);
    const wildcard = set.has('*');
    return (key) => wildcard || set.has(key);
  }, [me]);
}

// ─── Generic tab factory ────────────────────────────────────────
function MasterTab({
  title, gridId, api, buildColumns, ModalComponent,
  permPrefix, codeKey, nameKey, deleteLabel, deleteSystemMessage,
  emptyTitle, emptyText, exportFilename, addButtonText, searchPlaceholder
}) {
  const hasPerm = usePerms();
  const canCreate = hasPerm(`${permPrefix}.create`);
  const canUpdate = hasPerm(`${permPrefix}.update`);
  const canDelete = hasPerm(`${permPrefix}.delete`);

  const adapter = useMemo(() => ({ list: (q) => api.list(q) }), [api]);
  const { gridHandlers, refetch, query } = useErpMasterQuery({
    adapter,
    pageSize: 25,
    onError: (err) => {
      const msg = err?.response?.data?.error?.message ||
        err?.message || `Failed to load ${title.toLowerCase()}.`;
      message.error({ content: msg, key: `${gridId}-list` });
    }
  });

  const currentPage = query && query.page     ? query.page     : 1;
  const pageSize    = query && query.pageSize ? query.pageSize : 25;
  const columns = useMemo(
    () => buildColumns({ currentPage, pageSize }),
    [buildColumns, currentPage, pageSize]
  );

  const [modalOpen, setModalOpen]     = useState(false);
  const [modalMode, setModalMode]     = useState('add');
  const [modalRecord, setModalRecord] = useState(null);

  const openAdd = useCallback(() => {
    setModalMode('add'); setModalRecord(null); setModalOpen(true);
  }, []);
  const openEditOrView = useCallback(async (record, mode) => {
    try {
      const full = await api.get(record.id);
      setModalMode(mode); setModalRecord(full); setModalOpen(true);
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to load.');
    }
  }, [api]);
  const openEdit = useCallback((record) => openEditOrView(record, 'edit'), [openEditOrView]);
  const openView = useCallback((record) => openEditOrView(record, 'view'), [openEditOrView]);
  const closeModal = useCallback(() => setModalOpen(false), []);
  const onSaved = useCallback(async () => {
    setModalOpen(false);
    await refetch();
  }, [refetch]);

  const handleStatusChange = useCallback(async (record, nextActive) => {
    try {
      await api.updateStatus(record.id, { isActive: nextActive });
      message.success(nextActive ? 'Activated.' : 'Deactivated.');
      await refetch();
    } catch (err) {
      message.error(err?.response?.data?.error?.message || 'Failed to update status.');
    }
  }, [api, refetch]);

  const handleDelete = useCallback(async (record) => {
    if (record.isSystem) {
      message.warning(deleteSystemMessage);
      return;
    }
    const confirmed = await new Promise((resolve) => {
      let okClicked = false;
      Modal.confirm({
        title: `${deleteLabel}?`,
        icon: <ExclamationCircleOutlined />,
        content: `${record[codeKey]} · ${record[nameKey]} will be soft-deleted and hidden from pickers.`,
        okText: 'Delete',
        okButtonProps: { danger: true },
        cancelText: 'Cancel',
        onOk: () => { okClicked = true; },
        onCancel: () => { okClicked = false; },
        afterClose: () => resolve(okClicked)
      });
    });
    if (!confirmed) return;
    try {
      await api.remove(record.id);
      message.success('Deleted.');
      await refetch();
    } catch (err) {
      message.error(err?.response?.data?.error?.message || 'Failed to delete.');
    }
  }, [api, refetch, deleteLabel, deleteSystemMessage, codeKey, nameKey]);

  const actionsProp = useMemo(() => ({
    add:    canCreate,
    edit:   canUpdate,
    view:   true,
    delete: canDelete
  }), [canCreate, canUpdate, canDelete]);

  const contextMenuActions = useMemo(() => {
    const items = [
      { key: 'view', label: 'View', icon: <EyeOutlined />, onClick: openView }
    ];
    if (canUpdate) {
      items.push({ key: 'edit', label: 'Edit', icon: <EditOutlined />, onClick: openEdit });
      items.push({
        key: 'activate', label: 'Activate', icon: <CheckCircleOutlined />,
        onClick: (r) => handleStatusChange(r, true),
        visible: (r) => r && r.isActive === false
      });
      items.push({
        key: 'deactivate', label: 'Deactivate', icon: <PoweroffOutlined />,
        onClick: (r) => handleStatusChange(r, false),
        visible: (r) => r && r.isActive === true
      });
    }
    if (canDelete) {
      items.push({
        key: 'delete', label: 'Delete', icon: <DeleteOutlined />, danger: true,
        onClick: handleDelete,
        visible: (r) => r && r.isSystem !== true
      });
    }
    return items;
  }, [canUpdate, canDelete, openView, openEdit, handleDelete, handleStatusChange]);

  return (
    <>
      <ErpDataGrid
        title={title}
        gridId={gridId}
        columns={columns}
        rowKey="id"
        rowHeight="compact"
        stickyHeader
        searchable
        searchPlaceholder={searchPlaceholder}
        searchShortcut
        exportable
        exportFilename={exportFilename}
        actionWidth={56}
        actions={actionsProp}
        addButtonText={addButtonText}
        addButtonIcon={<PlusOutlined />}
        onAdd={canCreate ? openAdd : undefined}
        onEdit={canUpdate ? openEdit : undefined}
        onView={openView}
        onRowClick={openView}
        onDelete={canDelete ? handleDelete : undefined}
        contextMenuActions={contextMenuActions}
        emptyTitle={emptyTitle}
        emptyText={emptyText}
        emptyCtaLabel={canCreate ? addButtonText : undefined}
        {...gridHandlers}
      />
      <ModalComponent
        open={modalOpen}
        mode={modalMode}
        record={modalRecord}
        onClose={closeModal}
        onSaved={onSaved}
      />
    </>
  );
}

// ─── Page shell — three tabs ────────────────────────────────────
export default function ItemConfigPage() {
  const items = useMemo(() => [
    {
      key: 'categories',
      label: <span><AppstoreOutlined /> Categories</span>,
      children: (
        <MasterTab
          title="Item Categories"
          gridId="erp-masters-item-categories-v1"
          api={categoryApi}
          buildColumns={buildCategoryColumns}
          ModalComponent={ItemCategoryModal}
          permPrefix="erp.masters.item_categories"
          codeKey="categoryCode"
          nameKey="categoryName"
          deleteLabel="Delete category"
          deleteSystemMessage="System categories cannot be deleted. Deactivate it instead."
          emptyTitle="No categories yet"
          emptyText="System categories seed automatically. If this list is empty, contact admin."
          exportFilename="item-categories"
          addButtonText="New Category"
          searchPlaceholder="Search by code / name / parent…"
        />
      )
    },
    {
      key: 'material-types',
      label: <span><BlockOutlined /> Material Types</span>,
      children: (
        <MasterTab
          title="Material Types"
          gridId="erp-masters-material-types-v1"
          api={materialTypeApi}
          buildColumns={buildMaterialTypeColumns}
          ModalComponent={MaterialTypeModal}
          permPrefix="erp.masters.item_material_types"
          codeKey="materialTypeCode"
          nameKey="materialTypeName"
          deleteLabel="Delete material type"
          deleteSystemMessage="System material types cannot be deleted. Deactivate it instead."
          emptyTitle="No material types yet"
          emptyText="System material types seed automatically with category links + template defaults."
          exportFilename="item-material-types"
          addButtonText="New Material Type"
          searchPlaceholder="Search by code / name / category…"
        />
      )
    },
    {
      key: 'polish-types',
      label: <span><BgColorsOutlined /> Polish Types</span>,
      children: (
        <MasterTab
          title="Polish Types"
          gridId="erp-masters-polish-types-v1"
          api={polishTypeApi}
          buildColumns={buildPolishTypeColumns}
          ModalComponent={PolishTypeModal}
          permPrefix="erp.masters.item_polish_types"
          codeKey="polishTypeCode"
          nameKey="polishTypeName"
          deleteLabel="Delete polish type"
          deleteSystemMessage="System polish types cannot be deleted. Deactivate it instead."
          emptyTitle="No polish types yet"
          emptyText="Seven seeded polish vocabularies — NONE / CEP / RG / FP / PP / BEVEL / ARRIS."
          exportFilename="item-polish-types"
          addButtonText="New Polish Type"
          searchPlaceholder="Search by code / name / description…"
        />
      )
    },
    {
      key: 'brands',
      label: <span><ShopOutlined /> Brands</span>,
      children: (
        <MasterTab
          title="Brands"
          gridId="erp-masters-brands-v1"
          api={brandApi}
          buildColumns={buildBrandColumns}
          ModalComponent={BrandModal}
          permPrefix="erp.masters.item_brands"
          codeKey="brandCode"
          nameKey="brandName"
          deleteLabel="Delete brand"
          deleteSystemMessage="System brands cannot be deleted. Deactivate it instead."
          emptyTitle="No brands yet"
          emptyText="Add the brands you stock — pickers on Item / Glass / Hardware / Profile popups feed off this list."
          exportFilename="item-brands"
          addButtonText="New Brand"
          searchPlaceholder="Search by code / name / display name…"
        />
      )
    }
  ], []);

  return (
    <ErpMasterShell>
      {/* Stage UI-POLISH-LAYOUT-01 — `.app-page-shell` adds responsive
          internal padding so the Title + Tabs don't touch the white-card
          border. Same ramp as `.erp-accounts-page`. */}
      <div className="app-page-shell">
        <Title level={4} style={{ marginTop: 0, marginBottom: 8 }}>Item Configuration</Title>
        <Tabs
          items={items}
          defaultActiveKey="categories"
          size="middle"
          destroyInactiveTabPane
        />
      </div>
    </ErpMasterShell>
  );
}
